import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Card from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';

const OccupancyPredictionChart = ({ predictions = [], aiAnalysis = null, loading = false }) => {
  const chartData = predictions?.map(p => ({
    month: p?.month || 'N/A',
    occupancy: p?.predictedOccupancy || 0,
    current: p?.currentOccupancy || 0
  })) || [];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': case'critical':
        return 'text-danger-600';
      case 'medium':
        return 'text-warning-600';
      default:
        return 'text-success-600';
    }
  };

  const getRiskBgColor = (risk) => {
    switch (risk) {
      case 'high': case'critical':
        return 'bg-danger-50 border-danger-200';
      case 'medium':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Analysis Card */}
      {aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Icon name="Brain" className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">OpenAI Occupancy Analysis</h3>
              <p className="text-sm text-muted-foreground">GPT-5 Powered Insights</p>
            </div>
          </div>

          <div className={`p-4 rounded-lg border mb-4 ${getRiskBgColor(aiAnalysis?.riskLevel)}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {aiAnalysis?.riskLevel?.toUpperCase()} RISK LEVEL
              </span>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getRiskColor(aiAnalysis?.riskLevel)}`}>
                  {aiAnalysis?.predictedOccupancyRate?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Predicted Rate (3 months)</div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Lightbulb" className="w-4 h-4" />
              AI Insights
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {aiAnalysis?.insights}
            </p>
          </div>

          {aiAnalysis?.recommendations?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="CheckSquare" className="w-4 h-4" />
                Recommended Actions
              </h4>
              <ul className="space-y-2">
                {aiAnalysis?.recommendations?.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="ArrowRight" className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {loading && !aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Analyzing with OpenAI...</p>
            </div>
          </div>
        </Card>
      )}

      {/* Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Occupancy Trend Forecast</h3>
        {chartData?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorOccupancy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="current" 
                stroke="#10b981" 
                fillOpacity={1} 
                fill="url(#colorCurrent)" 
                name="Current Occupancy"
              />
              <Area 
                type="monotone" 
                dataKey="occupancy" 
                stroke="#3b82f6" 
                fillOpacity={1} 
                fill="url(#colorOccupancy)" 
                name="Predicted Occupancy"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="TrendingDown" className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No occupancy prediction data available</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OccupancyPredictionChart;